import { api } from '../../services/api';

const QUALITY_CONFIG_CACHE_KEY = 'senerfi_quality_print_config_v1';
const QUALITY_CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;

export const QUALITY_PRINT_DEFAULTS = Object.freeze({
  hv_activos: {
    document_key: 'hv_activos',
    document_name: 'Hoja de vida activos TI',
    code: 'FT-TIC-053',
    version: '00',
    effective_date: '2026-02-02',
    description: 'Formato hoja de vida de activos tecnologicos.',
    sort_order: 10,
    active: 1,
  },
  movement: {
    document_key: 'movement',
    document_name: 'Gestion de insumos',
    code: 'FT-ALM-116',
    version: '3',
    effective_date: '2026-01-14',
    description: 'Comprobante de gestion de insumos.',
    sort_order: 20,
    active: 1,
  },
  responsable_dotacion: {
    document_key: 'responsable_dotacion',
    document_name: 'Acta entrega dotacion',
    code: 'FT-SIG-082',
    version: '',
    effective_date: '',
    description: 'Resumen de asignaciones por responsable.',
    sort_order: 30,
    active: 1,
  },
  vehicle_preoperacional: {
    document_key: 'vehicle_preoperacional',
    document_name: 'Acta asignacion elementos vehiculo',
    code: 'FT-TRP-056',
    version: '05',
    effective_date: '2026-04-13',
    description: 'Acta de asignacion y validacion de elementos del vehiculo.',
    sort_order: 40,
    active: 1,
  },
  purchase_order: {
    document_key: 'purchase_order',
    document_name: 'Orden de compra',
    code: '',
    version: '',
    effective_date: '',
    description: 'Formato PDF oficial de orden de compra.',
    sort_order: 50,
    active: 1,
  },
  expense_request: {
    document_key: 'expense_request',
    document_name: 'Solicitud gastos de viaje',
    code: 'FT-CONT-146',
    version: '003',
    effective_date: '2026-02-04',
    description: 'Formato de solicitud de gastos de viaje.',
    sort_order: 60,
    active: 1,
  },
  hr_novelty: {
    document_key: 'hr_novelty',
    document_name: 'Solicitud novedades personal',
    code: 'FT-GTH-022',
    version: '1',
    effective_date: '2025-12-09',
    description: 'PDF de novedades de RRHH (permisos, incapacidades, compensatorios) con firmas de colaborador, jefe inmediato y Talento Humano.',
    sort_order: 70,
    active: 1,
  },
});

let memoryCache = null;
let memoryCacheAt = 0;

function toText(value) {
  return String(value ?? '').trim();
}

export function formatQualityDate(value) {
  const raw = toText(value);
  if (!raw) return '';
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return raw;
}

function normalizeRow(row = {}, fallback = {}) {
  const effectiveDate = toText(row.effective_date || fallback.effective_date);
  return {
    ...fallback,
    ...row,
    document_key: toText(row.document_key || fallback.document_key),
    document_name: toText(row.document_name || fallback.document_name),
    code: toText(row.code || fallback.code),
    version: toText(row.version || fallback.version),
    effective_date: effectiveDate,
    effective_date_display: formatQualityDate(effectiveDate),
    description: toText(row.description || fallback.description),
    sort_order: Number(row.sort_order ?? fallback.sort_order ?? 0) || 0,
    active: Number(row.active ?? fallback.active ?? 1) || 0,
  };
}

function mergeQualityRows(rows = []) {
  const map = {};
  for (const [key, fallback] of Object.entries(QUALITY_PRINT_DEFAULTS)) {
    map[key] = normalizeRow({ document_key: key }, fallback);
  }
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = toText(row?.document_key);
    if (!key) continue;
    const fallback = QUALITY_PRINT_DEFAULTS[key] || { document_key: key };
    map[key] = normalizeRow(row, fallback);
  }
  return map;
}

function readStorageCache() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(QUALITY_CONFIG_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const cachedAt = Number(parsed?.cachedAt || 0);
    if (!cachedAt || (Date.now() - cachedAt) > QUALITY_CONFIG_CACHE_TTL_MS) return null;
    return mergeQualityRows(parsed?.rows || []);
  } catch (_) {
    return null;
  }
}

function writeStorageCache(rows = []) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(QUALITY_CONFIG_CACHE_KEY, JSON.stringify({
      cachedAt: Date.now(),
      rows,
    }));
  } catch (_) {}
}

export function clearQualityPrintConfigCache() {
  memoryCache = null;
  memoryCacheAt = 0;
  if (typeof window !== 'undefined') {
    try { window.localStorage.removeItem(QUALITY_CONFIG_CACHE_KEY); } catch (_) {}
  }
}

export async function getQualityPrintConfigMap({ force = false } = {}) {
  if (!force && memoryCache && (Date.now() - memoryCacheAt) <= QUALITY_CONFIG_CACHE_TTL_MS) {
    return memoryCache;
  }

  if (!force) {
    const stored = readStorageCache();
    if (stored) {
      memoryCache = stored;
      memoryCacheAt = Date.now();
      return stored;
    }
  }

  try {
    const response = await api.admin.qualityVersions.list({ active: 1, limit: 500 });
    const rows = Array.isArray(response?.data) ? response.data : [];
    const merged = mergeQualityRows(rows);
    memoryCache = merged;
    memoryCacheAt = Date.now();
    writeStorageCache(rows);
    return merged;
  } catch (_) {
    const fallback = mergeQualityRows([]);
    memoryCache = fallback;
    memoryCacheAt = Date.now();
    return fallback;
  }
}

export async function getQualityPrintMeta(documentKey, overrides = {}) {
  const key = toText(documentKey);
  const map = await getQualityPrintConfigMap();
  const fallback = QUALITY_PRINT_DEFAULTS[key] || { document_key: key };
  return normalizeRow({
    ...(map?.[key] || fallback),
    ...overrides,
    document_key: key || fallback.document_key,
  }, fallback);
}

export function getDefaultQualityPrintRows() {
  return Object.values(QUALITY_PRINT_DEFAULTS).map((row) => normalizeRow(row, row));
}
